import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { useSelector } from 'react-redux';
import { DOMAIN } from '../redux/constants';
import buildAuthHeaders from '../utils/apiHeaders';

/**
 * One streak payload, ACTUALLY shared by every surface that needs it (navbar
 * pill, streak sheet, daily quest ring, the Tier-2 gate on the vote page).
 *
 * This used to be a per-component useState: three mounted copies plus a
 * fourth fetcher, each with private state. VotePage's refreshStreak() after
 * a vote updated only VotePage's copy — the navbar pill and the comment-lock
 * counter stayed stale until a window-focus refetch or remount, which is why
 * the pill could say "2 of 3 votes today" after the 3rd vote. The store now
 * lives at module scope with a subscriber list, so one refresh() updates
 * every consumer.
 *
 * `GET /engagement/streak/` has three response shapes: the anonymous branch,
 * the no-VoteStreak-row branch, and the full `formatStreakResponse`. Only the
 * last emits `last_vote_timestamp`, `recovery_expires_at`,
 * `recovery_votes_count`, and `recovery_votes_goal`, so every consumer must
 * tolerate those being undefined.
 */
let store = { data: null, loading: true, access: undefined };
let inFlight = null;
const listeners = new Set();
const emit = () => listeners.forEach((l) => l(store));

// Client-local day key, matching the server's `dayKeyForOffset(getTimezoneOffset())`
// — the same dayspace `last_vote_date` is written in, so the two compare
// directly.
const localDayKey = () => {
  const now = new Date();
  return new Date(now.getTime() - now.getTimezoneOffset() * 60_000)
    .toISOString()
    .slice(0, 10);
};

// Monotonic write-intent counter. Every issued fetch and every accepted
// vote-snapshot bumps it; a resolving request whose captured generation is
// no longer current lost the race to a newer write and must drop its
// payload. Without this, a slow ANON request issued pre-login could resolve
// after the post-login force-fetch and clobber the authed payload — and
// since store.access already matched the new token, nothing corrected it
// until a focus refetch.
let generation = 0;

// Last local day key we wrote data under. Module state (not tick state) so
// it SURVIVES the interval being stopped while no consumer is mounted: a
// midnight crossing during that gap is detected on the next mount and
// forces a refetch, instead of silently re-seeding to the new day and
// showing "3 of 3 today" on a zero-vote day.
let lastSeenDay = localDayKey();

// Write-through for the vote response's `streak` payload: the vote POST
// returns the freshest streak state there is, and it used to live only on
// `question.streak` inside the questions slice — a second truth the reveal
// panel rendered while the navbar read this store. Non-React callers
// (redux actions) push it here so every consumer updates at once.
export const pushStreakSnapshot = (data) => {
  if (!data || typeof data.current_streak !== 'number') return;
  // The vote handler's anon branch returns a 3-key stub; never let it
  // clobber a full authed payload.
  if (store.data && data.daily_vote_count === undefined) return;
  // Stale-day guard: a choice-flip vote early-returns the streak row
  // UNMUTATED — after a midnight crossing that row still carries the
  // previous day's key and that day's daily_vote_count, while the GET path
  // zeroed the store's count at read time. Never let a snapshot keyed to a
  // previous local day RAISE the daily count. (A parallel server-side fix
  // zeroes the flip payload too; this is defense-in-depth and tolerates
  // both server behaviors — a same-day or already-zeroed snapshot passes.)
  if (
    data.last_vote_date &&
    data.last_vote_date !== localDayKey() &&
    (data.daily_vote_count ?? 0) > (store.data?.daily_vote_count ?? 0)
  ) {
    return;
  }
  generation += 1; // invalidate any GET issued before this fresher payload
  lastSeenDay = localDayKey();
  store = { ...store, data: { ...store.data, ...data }, loading: false };
  emit();
};

const fetchShared = (access, { force = false } = {}) => {
  // Concurrent mounts join the same request; an explicit refresh (force)
  // starts a new one.
  if (inFlight && !force) return inFlight;
  generation += 1;
  const gen = generation;
  const issuedAccess = access;
  const req = axios
    .get(`${DOMAIN}/api/v1/engagement/streak/`, buildAuthHeaders({ access }))
    .then((res) => {
      // Drop out-of-order writes: if a newer fetch/snapshot was issued, or
      // the identity changed, while this request was in flight, this
      // response is stale — last ISSUED wins, not last RESOLVED.
      if (gen !== generation || store.access !== issuedAccess) return res.data;
      lastSeenDay = localDayKey();
      store = { ...store, data: res.data, loading: false };
      emit();
      return res.data;
    })
    .catch(() => {
      if (gen !== generation || store.access !== issuedAccess) return null;
      store = { ...store, loading: false };
      emit();
      return null;
    })
    .finally(() => {
      if (inFlight === req) inFlight = null;
    });
  inFlight = req;
  return req;
};

// Wall-clock re-evaluation: at_risk is a pure function of hours-since-vote
// and the daily ring resets at local midnight, but nothing else re-renders
// on those boundaries — a tab left open never showed the warning state and
// never rolled the day over. One shared 60s tick: re-emit so consumers
// re-derive, and refetch when the local date changes. The focus refetch
// covers backgrounded tabs; the interval's job is the long-open tab.
let tick = null;
const startTick = () => {
  const day = localDayKey();
  if (lastSeenDay !== day) {
    // The local day rolled over while no consumer was mounted (interval
    // stopped) — refetch now instead of silently adopting the new day.
    lastSeenDay = day;
    // If the mounting effect already has a request in flight, its response
    // is post-rollover anyway (the server rolls the day at read time).
    if (!inFlight) fetchShared(store.access, { force: true });
  }
  if (tick) return;
  tick = setInterval(() => {
    if (listeners.size === 0) return;
    const tickDay = localDayKey();
    if (tickDay !== lastSeenDay) {
      lastSeenDay = tickDay;
      fetchShared(store.access, { force: true });
      return;
    }
    // Reassign to a fresh object: emit() hands listeners the store
    // reference, and React bails out of setState on identical references —
    // re-emitting the SAME object never re-renders, which made the at-risk
    // and midnight re-derives silent no-ops.
    store = { ...store };
    emit();
  }, 60_000);
};
const stopTick = () => {
  if (tick && listeners.size === 0) {
    clearInterval(tick);
    tick = null;
  }
};


export default function useStreak() {
  const { userInfo } = useSelector((state) => state.userLogin);
  const access = userInfo?.access;
  const [snap, setSnap] = useState(store);

  useEffect(() => {
    const listener = (s) => setSnap(s);
    listeners.add(listener);
    if (store.access !== access) {
      // Identity changed (login/logout/registration): the previous user's
      // payload must not linger on the new identity's surfaces.
      store = { data: null, loading: true, access };
      emit();
      fetchShared(access, { force: true });
    } else if (store.data === null) {
      fetchShared(access);
    } else {
      setSnap(store);
    }
    // Focus refetch: a vote cast in another tab should reach this one. Not
    // forced, so N mounted consumers produce one request, not N.
    const onFocus = () => fetchShared(access);
    window.addEventListener('focus', onFocus);
    startTick();
    return () => {
      listeners.delete(listener);
      window.removeEventListener('focus', onFocus);
      stopTick();
    };
  }, [access]);

  const refresh = useCallback(() => fetchShared(access, { force: true }), [access]);
  const setStreak = useCallback((data) => {
    generation += 1; // a direct write is a write-intent like any other
    store = { ...store, data };
    emit();
  }, []);

  return { streak: snap.data, loading: snap.loading, refresh, setStreak };
}
